/**
 * HTTP transport for sending telemetry to Nova's API Gateway.
 * Uses HMAC-SHA256 signing with the publishable key (same pattern as widget API client).
 * Zero AWS SDK dependency — pure fetch().
 */

import type { LogEvent } from '../types.js';
import type { WriterBackend } from './types.js';

const SDK_VERSION = '1.0.0';

export class HttpTransport implements WriterBackend {
  private apiUrl: string;
  private publishableKey: string;
  private appId: string;
  private sessionId: string | null = null;

  constructor(config: { apiUrl: string; publishableKey: string; appId: string }) {
    this.apiUrl = config.apiUrl;
    this.publishableKey = config.publishableKey;
    this.appId = config.appId;
  }

  async writeBatch(events: LogEvent[]): Promise<void> {
    if (events.length === 0) return;

    const body = JSON.stringify({
      appId: this.appId,
      sdkVersion: SDK_VERSION,
      sessionId: this.getSessionId(),
      events: events.map((e) => ({
        stream: e.stream,
        timestamp: new Date().toISOString(),
        payload: e.data,
      })),
    });

    // Sign empty string: API Gateway REQUEST authorizers don't receive the body,
    // so the auth Lambda always verifies HMAC('') regardless of HTTP method.
    const signature = await this.computeHmac('');

    const response = await fetch(`${this.apiUrl}/sdk/v1/telemetry`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-Nova-Api-Key': this.publishableKey,
        'X-Nova-Signature': signature,
      },
      body,
    });

    if (!response.ok) {
      throw new Error(`Telemetry delivery failed: ${response.status}`);
    }
  }

  private async computeHmac(body: string): Promise<string> {
    const encoder = new TextEncoder();
    const key = await crypto.subtle.importKey(
      'raw',
      encoder.encode(this.publishableKey),
      { name: 'HMAC', hash: 'SHA-256' },
      false,
      ['sign'],
    );
    const sig = await crypto.subtle.sign('HMAC', key, encoder.encode(body));
    return Array.from(new Uint8Array(sig))
      .map((b) => b.toString(16).padStart(2, '0'))
      .join('');
  }

  private getSessionId(): string {
    if (!this.sessionId) {
      this.sessionId =
        typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function'
          ? crypto.randomUUID().slice(0, 8)
          : Math.random().toString(36).slice(2, 10);
    }
    return this.sessionId;
  }
}
